window.theme = window.theme || {};

theme.events = {
  on(eventName, callback) {
    document.addEventListener(eventName, callback);
  },

  off(eventName, callback) {
    document.removeEventListener(eventName, callback);
  },

  emit(eventName, detail = {}) {
    document.dispatchEvent(new CustomEvent(eventName, { detail }));
  }
};

theme.utils = {
  trapFocus(container) {
    if (!container) return () => {};

    const focusable = container.querySelectorAll(
      'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );
    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    const handleKeydown = (event) => {
      if (event.key !== 'Tab' || !first || !last) return;

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    container.addEventListener('keydown', handleKeydown);
    first?.focus();

    return () => container.removeEventListener('keydown', handleKeydown);
  },

  lockScroll() {
    document.documentElement.classList.add('is-scroll-locked');
    document.body.classList.add('is-scroll-locked');
  },

  unlockScroll() {
    document.documentElement.classList.remove('is-scroll-locked');
    document.body.classList.remove('is-scroll-locked');
  }
};

theme.scroll = {
  init() {
    const update = () => {
      const scrollY = window.scrollY || window.pageYOffset;
      document.documentElement.classList.toggle('is-scrolled', scrollY > 16);
      document.documentElement.style.setProperty('--scroll-y', String(scrollY));
      theme.events.emit('theme:scroll', { scrollY });
    };

    update();
    let ticking = false;
    window.addEventListener('scroll', () => {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(() => {
        update();
        ticking = false;
      });
    }, { passive: true });
  }
};

theme.animations = {
  init() {
    this.prepareTextReveal();
    this.prepareStaggers();

    const revealItems = document.querySelectorAll('[data-animate], [data-text-reveal], [data-stagger]');
    if (!revealItems.length) return;

    if (!('IntersectionObserver' in window)) {
      revealItems.forEach((item) => item.classList.add('is-visible'));
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -12% 0px', threshold: 0.12 }
    );

    revealItems.forEach((item) => observer.observe(item));
  },

  prepareTextReveal() {
    const candidates = document.querySelectorAll(
      '.hero-section__heading, .type-title, .brand-story__heading, .editorial-page__intro h1, .contact-editorial__intro h1, .faq-editorial__intro h1, .main-product__title, .home-campaign-banner__content h2, .editorial-story-strip__content h2, .editorial-tile__content h3, .como-homepage__heading, .como-homepage__story-copy h2'
    );

    candidates.forEach((element) => {
      if (element.dataset.textRevealPrepared === 'true') return;
      if (element.children.length > 0) return;

      const text = element.textContent.trim();
      if (!text) return;

      element.dataset.textReveal = '';
      element.dataset.textRevealPrepared = 'true';
      element.textContent = '';

      text.split(/\s+/).forEach((word, index, words) => {
        const span = document.createElement('span');
        span.className = 'text-reveal__word';
        span.style.setProperty('--word-index', index);
        span.textContent = word;
        element.appendChild(span);

        if (index < words.length - 1) {
          element.appendChild(document.createTextNode(' '));
        }
      });
    });
  },

  prepareStaggers() {
    const containers = document.querySelectorAll(
      '.main-collection__grid, .product-grid-section__grid, .editorial-featured-products__grid, .featured-products__scroller, .editorial-story-strip, .editorial-collection-grid__items, .faq-editorial__items, .site-footer__links'
      + ', .como-homepage__grid, .como-homepage__categories, .como-homepage__ugc-grid'
    );

    containers.forEach((container) => {
      if (container.dataset.staggerPrepared === 'true') return;

      container.dataset.stagger = '';
      container.dataset.staggerPrepared = 'true';

      Array.from(container.children).forEach((child, index) => {
        child.style.setProperty('--stagger-index', index);
      });
    });
  }
};

theme.pageTransitions = {
  init() {
    document.body.classList.add('is-loading');
    window.requestAnimationFrame(() => {
      document.body.classList.remove('is-loading');
    });

    document.addEventListener('click', (event) => {
      const link = event.target.closest('a[href]');
      if (!link || event.defaultPrevented) return;
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      if (link.target && link.target !== '_self') return;
      if (link.hasAttribute('download')) return;

      const url = new URL(link.href, window.location.href);
      if (url.origin !== window.location.origin) return;
      if (url.pathname === window.location.pathname && url.search === window.location.search && url.hash) return;

      event.preventDefault();
      document.body.classList.add('is-leaving');

      window.setTimeout(() => {
        window.location.href = url.href;
      }, 180);
    });

    window.addEventListener('pageshow', () => {
      document.body.classList.remove('is-leaving', 'is-loading');
    });
  }
};

theme.menu = {
  activeCleanup: null,

  init() {
    document.querySelectorAll('.theme-menu-drawer__nav').forEach((nav) => {
      Array.from(nav.children).forEach((link, index) => {
        link.style.setProperty('--drawer-index', index);
      });
    });

    document.querySelectorAll('[data-menu-toggle]').forEach((toggle) => {
      const targetSelector = toggle.getAttribute('data-menu-toggle');
      const target = targetSelector ? document.querySelector(targetSelector) : document.querySelector('[data-menu-drawer]');
      if (!target) return;

      toggle.addEventListener('click', () => {
        const isOpen = target.getAttribute('aria-hidden') === 'false';
        isOpen ? this.close(target, toggle) : this.open(target, toggle);
      });
    });

    document.querySelectorAll('[data-menu-close]').forEach((closeButton) => {
      closeButton.addEventListener('click', () => {
        const drawer = closeButton.closest('[data-menu-drawer]');
        this.close(drawer);
      });
    });
  },

  open(drawer, toggle) {
    if (!drawer) return;
    drawer.setAttribute('aria-hidden', 'false');
    drawer.classList.add('is-open');
    toggle?.setAttribute('aria-expanded', 'true');
    document.querySelector('[data-theme-header]')?.classList.add('is-drawer-open');
    theme.utils.lockScroll();
    this.activeCleanup = theme.utils.trapFocus(drawer);
    theme.events.emit('theme:menu:open', { drawer });
  },

  close(drawer, toggle) {
    if (!drawer) return;
    drawer.setAttribute('aria-hidden', 'true');
    drawer.classList.remove('is-open');
    const controls = drawer.id ? document.querySelectorAll(`[data-menu-toggle="#${drawer.id}"]`) : [];
    controls.forEach((control) => control.setAttribute('aria-expanded', 'false'));
    toggle?.setAttribute('aria-expanded', 'false');
    document.querySelector('[data-theme-header]')?.classList.remove('is-drawer-open');
    theme.utils.unlockScroll();
    this.activeCleanup?.();
    this.activeCleanup = null;
    theme.events.emit('theme:menu:close', { drawer });
  }
};

theme.cartDrawer = {
  drawer: null,
  cleanupFocus: null,
  moneyFormat: '${{amount}}',

  init() {
    this.drawer = document.querySelector('[data-cart-drawer]');
    if (!this.drawer) return;

    document.querySelectorAll('[data-cart-open]').forEach((button) => {
      button.addEventListener('click', (event) => {
        event.preventDefault();
        this.open();
      });
    });

    document.querySelectorAll('[data-cart-close]').forEach((button) => {
      button.addEventListener('click', () => this.close());
    });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') this.close();
    });

    this.drawer.addEventListener('click', (event) => {
      const item = event.target.closest('[data-cart-item]');
      if (!item) return;

      if (event.target.matches('[data-cart-increase]')) {
        this.changeItem(item.dataset.key, Number(item.dataset.quantity) + 1);
      }

      if (event.target.matches('[data-cart-decrease]')) {
        this.changeItem(item.dataset.key, Math.max(0, Number(item.dataset.quantity) - 1));
      }

      if (event.target.matches('[data-cart-remove]')) {
        this.changeItem(item.dataset.key, 0);
      }
    });

    document.addEventListener('submit', (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement) || form.action.indexOf('/cart/add') === -1) return;

      event.preventDefault();
      this.addFromForm(form);
    });
  },

  async open() {
    if (!this.drawer) return;
    this.drawer.setAttribute('aria-hidden', 'false');
    this.drawer.classList.add('is-open');
    theme.utils.lockScroll();
    this.cleanupFocus = theme.utils.trapFocus(this.drawer);
    await this.refresh();
    theme.events.emit('theme:cart:open', { drawer: this.drawer });
  },

  close() {
    if (!this.drawer) return;
    this.drawer.setAttribute('aria-hidden', 'true');
    this.drawer.classList.remove('is-open');
    theme.utils.unlockScroll();
    this.cleanupFocus?.();
    this.cleanupFocus = null;
    theme.events.emit('theme:cart:close', { drawer: this.drawer });
  },

  async addFromForm(form) {
    const submit = form.querySelector('[type="submit"]');
    submit?.setAttribute('disabled', 'disabled');

    try {
      await fetch('/cart/add.js', {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: new FormData(form)
      });
      await this.open();
    } finally {
      submit?.removeAttribute('disabled');
    }
  },

  async changeItem(key, quantity) {
    await fetch('/cart/change.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      body: JSON.stringify({ id: key, quantity })
    });
    await this.refresh();
  },

  async refresh() {
    const response = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
    const cart = await response.json();
    this.render(cart);
    theme.events.emit('theme:cart:updated', { cart });
  },

  render(cart) {
    const itemsContainer = this.drawer.querySelector('[data-cart-items]');
    const footer = this.drawer.querySelector('[data-cart-footer]');
    const subtotal = this.drawer.querySelector('[data-cart-subtotal]');
    const template = document.querySelector('[data-cart-item-template]');

    itemsContainer.innerHTML = '';

    if (!cart.items.length) {
      itemsContainer.innerHTML = '<p class="cart-drawer__empty">Your cart is empty.</p>';
      footer.hidden = true;
      this.updateCartCount(cart.item_count);
      return;
    }

    cart.items.forEach((lineItem, index) => {
      const node = template.content.firstElementChild.cloneNode(true);
      node.dataset.key = lineItem.key;
      node.dataset.quantity = lineItem.quantity;
      node.style.setProperty('--be-motion-index', index);

      const image = node.querySelector('[data-cart-item-image]');
      const imageLink = node.querySelector('[data-cart-item-url]');
      imageLink.href = lineItem.url;
      if (lineItem.image) {
        image.src = `${lineItem.image}&width=180`;
        image.alt = lineItem.product_title;
      } else {
        image.remove();
      }

      node.querySelector('[data-cart-item-title]').textContent = lineItem.product_title;
      node.querySelector('[data-cart-item-title]').href = lineItem.url;
      node.querySelector('[data-cart-item-variant]').textContent = lineItem.variant_title || '';
      node.querySelector('[data-cart-item-quantity]').textContent = lineItem.quantity;
      node.querySelector('[data-cart-item-price]').textContent = this.formatMoney(lineItem.final_line_price);

      itemsContainer.appendChild(node);
    });

    subtotal.textContent = this.formatMoney(cart.total_price);
    footer.hidden = false;
    this.updateCartCount(cart.item_count);
  },

  updateCartCount(count) {
    document.querySelectorAll('[data-cart-count]').forEach((element) => {
      element.textContent = count;
      element.hidden = count === 0;
    });
  },

  formatMoney(cents) {
    return new Intl.NumberFormat(document.documentElement.lang || 'en', {
      style: 'currency',
      currency: window.Shopify?.currency?.active || 'USD'
    }).format(cents / 100);
  }
};

theme.featuredProducts = {
  init() {
    document.querySelectorAll('[data-featured-products]').forEach((section) => {
      if (section.dataset.carouselInitialized === 'true') return;

      const scroller = section.querySelector('[data-featured-scroller]');
      const previous = section.querySelector('[data-featured-prev]');
      const next = section.querySelector('[data-featured-next]');
      if (!scroller || !previous || !next) return;

      section.dataset.carouselInitialized = 'true';

      const scrollByAmount = () => Math.max(scroller.clientWidth * 0.85, 280);

      previous.addEventListener('click', () => {
        scroller.scrollBy({ left: -scrollByAmount(), behavior: 'smooth' });
      });

      next.addEventListener('click', () => {
        scroller.scrollBy({ left: scrollByAmount(), behavior: 'smooth' });
      });
    });
  }
};

theme.productRecommendations = {
  init() {
    document.querySelectorAll('[data-product-recommendations]').forEach((section) => {
      if (section.dataset.recommendationsLoaded === 'true') return;

      const url = section.dataset.url;
      if (!url) return;

      section.dataset.recommendationsLoaded = 'true';

      fetch(url)
        .then((response) => {
          if (!response.ok) throw new Error(response.status);
          return response.text();
        })
        .then((html) => {
          const documentFragment = new DOMParser().parseFromString(html, 'text/html');
          const recommendations = documentFragment.querySelector('[data-product-recommendations]');

          if (recommendations && recommendations.innerHTML.trim().length) {
            section.innerHTML = recommendations.innerHTML;
            section.setAttribute('data-featured-products', '');
            theme.featuredProducts.init();
            theme.animations.init();
          }
        })
        .catch(() => {
          section.hidden = true;
        });
    });
  }
};

theme.footer = {
  init() {
    const accordions = document.querySelectorAll('[data-footer-accordion]');
    if (!accordions.length) return;

    const desktopQuery = window.matchMedia('(min-width: 750px)');

    const syncAccordions = () => {
      accordions.forEach((accordion) => {
        accordion.open = desktopQuery.matches;
      });
    };

    syncAccordions();
    if (desktopQuery.addEventListener) {
      desktopQuery.addEventListener('change', syncAccordions);
    } else {
      desktopQuery.addListener(syncAccordions);
    }
  }
};

theme.collectionFilters = {
  init() {
    document.querySelectorAll('[data-filter-drawer]').forEach((drawer) => {
      const openButtons = document.querySelectorAll('[data-filter-open]');
      const closeButtons = drawer.querySelectorAll('[data-filter-close]');

      const open = () => {
        drawer.setAttribute('aria-hidden', 'false');
        drawer.classList.add('is-open');
        theme.utils.lockScroll();
      };

      const close = () => {
        drawer.setAttribute('aria-hidden', 'true');
        drawer.classList.remove('is-open');
        theme.utils.unlockScroll();
      };

      openButtons.forEach((button) => button.addEventListener('click', open));
      closeButtons.forEach((button) => button.addEventListener('click', close));
      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') close();
      });
    });
  }
};

theme.beAutystMotion = {
  init() {
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    document.documentElement.classList.add('be-motion-ready');

    this.prepareReveals();
    this.prepareCartPulse();
    this.preparePointerTilt(reduceMotion);
    this.prepareParallax(reduceMotion);
  },

  prepareReveals() {
    const selectors = [
      '.be-home__hero',
      '.be-home__product-breaker',
      '.be-home__editorial-split',
      '.be-home__review-banner',
      '.be-home__ugc',
      '.be-home__grades',
      '.be-home__stores',
      '.main-collection__banner',
      '.main-collection__header',
      '.main-collection__toolbar',
      '.main-product__media-item',
      '.main-product__info',
      '.recommended-products',
      '.editorial-featured-products',
      '.be-info-page__hero',
      '.be-info-page__card',
      '.be-info-page__placeholder'
    ];

    document.querySelectorAll(selectors.join(',')).forEach((element) => {
      if (!element.hasAttribute('data-animate')) element.setAttribute('data-animate', 'be');
    });

    document.querySelectorAll(
      '.be-home__product-grid, .main-collection__grid, .product-gallery, .be-info-page__grid, .be-home__ugc-track'
    ).forEach((container) => {
      Array.from(container.children).forEach((child, index) => {
        child.style.setProperty('--be-motion-index', index);
      });
    });
  },

  prepareCartPulse() {
    const cartLinks = document.querySelectorAll('[data-cart-open]');
    const pulse = () => {
      cartLinks.forEach((link) => {
        link.classList.remove('is-cart-wake');
        void link.offsetWidth;
        link.classList.add('is-cart-wake');
      });

      window.setTimeout(() => {
        cartLinks.forEach((link) => link.classList.remove('is-cart-wake'));
      }, 760);
    };

    theme.events.on('theme:cart:updated', pulse);
    theme.events.on('theme:cart:open', ({ detail }) => {
      detail?.drawer?.classList.add('is-cart-awake');
      window.setTimeout(() => detail?.drawer?.classList.remove('is-cart-awake'), 900);
    });
  },

  preparePointerTilt(reduceMotion) {
    if (reduceMotion || window.matchMedia('(hover: none)').matches) return;

    document.querySelectorAll('.main-product__media-item').forEach((item) => {
      item.addEventListener('pointermove', (event) => {
        const rect = item.getBoundingClientRect();
        const x = ((event.clientX - rect.left) / rect.width - 0.5) * 2;
        const y = ((event.clientY - rect.top) / rect.height - 0.5) * 2;
        item.style.setProperty('--be-tilt-x', `${(-y * 1.2).toFixed(2)}deg`);
        item.style.setProperty('--be-tilt-y', `${(x * 1.2).toFixed(2)}deg`);
      }, { passive: true });

      item.addEventListener('pointerleave', () => {
        item.style.setProperty('--be-tilt-x', '0deg');
        item.style.setProperty('--be-tilt-y', '0deg');
      });
    });
  },

  prepareParallax(reduceMotion) {
    if (reduceMotion) return;

    const targets = Array.from(document.querySelectorAll(
      '.be-home__hero-image, .main-collection__banner-image, .be-home__editorial-image, .main-product__image'
    ));
    if (!targets.length) return;

    let ticking = false;
    const update = () => {
      const viewport = window.innerHeight || 1;
      targets.forEach((target) => {
        const rect = target.getBoundingClientRect();
        if (rect.bottom < -120 || rect.top > viewport + 120) return;
        const progress = (rect.top + rect.height / 2 - viewport / 2) / viewport;
        target.style.setProperty('--be-parallax-y', `${Math.max(-18, Math.min(18, progress * -18)).toFixed(2)}px`);
      });
      ticking = false;
    };

    update();
    window.addEventListener('scroll', () => {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(update);
    }, { passive: true });

    window.addEventListener('resize', update);
  }
};



// BA header search compact
theme.headerSearch={init(){document.querySelectorAll('[data-header-search]').forEach(form=>{const toggle=form.querySelector('[data-header-search-toggle]'),input=form.querySelector('[data-header-search-input]'),toggleText=form.querySelector('[data-header-search-toggle-text]');if(!toggle||!input)return;const searchLabel=toggle.getAttribute('data-search-label')||toggleText?.textContent||'Search',closeLabel=toggle.getAttribute('data-close-label')||'Close';input.setAttribute('tabindex','-1');const setToggleLabel=(label)=>{if(toggleText)toggleText.textContent=label;toggle.setAttribute('aria-label',label)};const open=()=>{form.classList.add('is-open');toggle.setAttribute('aria-expanded','true');input.removeAttribute('tabindex');setToggleLabel(closeLabel);requestAnimationFrame(()=>input.focus())};const close=()=>{form.classList.remove('is-open');toggle.setAttribute('aria-expanded','false');input.setAttribute('tabindex','-1');setToggleLabel(searchLabel);input.blur();toggle.blur()};toggle.addEventListener('click',event=>{event.preventDefault();form.classList.contains('is-open')?close():open()});form.addEventListener('submit',event=>{if(!input.value.trim()){event.preventDefault();open()}});input.addEventListener('keydown',event=>{if(event.key==='Escape'){event.preventDefault();close()}});document.addEventListener('keydown',event=>{if(event.key==='Escape'&&form.classList.contains('is-open'))close()});document.addEventListener('click',event=>{if(form.classList.contains('is-open')&&!form.contains(event.target))close()})})}};

theme.init = () => {
  theme.pageTransitions.init();
  theme.scroll.init();
  theme.beAutystMotion?.init?.();
  theme.animations.init();
  theme.menu.init();
  theme.headerSearch?.init?.();
  theme.cartDrawer.init();
  theme.featuredProducts.init();
  theme.productRecommendations.init();
  theme.footer.init();
  theme.collectionFilters.init();
};

document.addEventListener('DOMContentLoaded', theme.init);
